/**
 * The 1–4 severity badge — TWO operator axes over ONE shared palette (#613
 * Threat/Response split, founder §4.3 sign-off 2026-07-10). Fork by CALLER, never
 * globally: pass `axis`.
 *
 *   • axis='response' (default) — the IC-ASSESSED Ontario IMS §5 Table 1 Response
 *     Level (Small … Emergency) on an OWNED incident. The default is deliberately the
 *     §5 axis so a caller must OPT IN to the machine axis — a forgotten caller stays on
 *     the owned-incident surface where "Response Level" is doctrine-correct.
 *   • axis='threat' — the MACHINE-DERIVED Threat Level (Low … Very High) on a RAW,
 *     un-owned hazard OR a derived roll-up (e.g. the SITREP response_level_max). Ontario
 *     IMS §5 "Response Level" is meaningful only once a response exists; a raw hazard has
 *     only a machine threat, so "Response Level" there is a §4.1 DOCTRINE-MISMATCH.
 *
 * Colour-coded 1 grey / 2 blue / 3 amber / 4 red (shared palette, keyed on the NUMBER)
 * with a tooltip summarising the axis criteria. Palette is context-locked — the hex
 * values + meta live in ./styles/responseLevel and must NOT be re-declared here
 * (operator-cop-doctrine §3A.7 / §8.4). Always renders the axis text so colour is never
 * load-bearing alone.
 */
import { RESPONSE_LEVEL_META, THREAT_LEVEL_META, isResponseLevel } from './styles/responseLevel';

/** The operator axis this badge renders. Default 'response' (the owned-incident §5 axis). */
export type ResponseLevelAxis = 'threat' | 'response';

/** `testid` overrides the default so a panel that renders more than one badge (e.g. the
 *  #445-ws2 incident header's operative level next to an annotated derived) gives each a
 *  distinct, targetable test id. */
export function ResponseLevelBadge({
  level,
  testid = 'response-level-badge',
  axis = 'response',
}: {
  level: number | null | undefined;
  testid?: string;
  axis?: ResponseLevelAxis;
}): JSX.Element | null {
  if (!isResponseLevel(level)) return null;
  const s = (axis === 'threat' ? THREAT_LEVEL_META : RESPONSE_LEVEL_META)[level];
  const prefix = axis === 'threat' ? 'Threat Level' : 'Response Level';
  return (
    <span
      data-testid={testid}
      data-level={level}
      data-axis={axis}
      className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded font-medium"
      style={{ backgroundColor: `${s.color}33`, color: s.color }}
      title={s.criteria}
    >
      {prefix} {level} · {s.name}
    </span>
  );
}
